import React, { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import '../AddDataPage/styles.css';
import SidebarLayout from '../../components/SidebarLayout';
import { API_BASE_URL as API_URL } from '../../config/apiBase';


const AdminAddDataPage = () => {
  const navigate = useNavigate();
  const { id: entityId, schemaId } = useParams();
  const [schema, setSchema] = useState(null);
  const [formData, setFormData] = useState({});
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [file, setFile] = useState(null);
  const [inputMode, setInputMode] = useState('manual');

  useEffect(() => {
    if (schemaId) {
      fetchSchema();
    }
  }, [schemaId]);

  const fetchSchema = async () => {
    try {
      const token = localStorage.getItem('token');
      // Use admin endpoint with entity_id query parameter
      const url = `${API_URL}/admin/schemas/${schemaId}?entity_id=${entityId}`;
      const response = await fetch(url, {
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });

      if (!response.ok) {
        throw new Error('Failed to fetch schema');
      }

      const data = await response.json();
      setSchema(data.data);
      
      const initialFormData = {};
      const columns = data.data.schema_definition.columns || [];
      columns.forEach(column => {
        initialFormData[column.name] = '';
      });
      setFormData(initialFormData);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  };

  const handleInputChange = (fieldName, value) => {
    setFormData(prev => ({
      ...prev,
      [fieldName]: value
    }));
  };

  const handleFileChange = (e) => {
    const selectedFile = e.target.files[0];
    if (selectedFile) {
      const allowedTypes = ['.csv', '.xlsx', '.xls'];
      const fileExt = selectedFile.name.toLowerCase().substring(selectedFile.name.lastIndexOf('.'));
      
      if (!allowedTypes.includes(fileExt)) {
        setError('Please select a CSV or Excel file');
        return;
      }

      if (selectedFile.size > 10 * 1024 * 1024) {
        setError('File size must be less than 10MB');
        return;
      }

      setFile(selectedFile);
      setError('');
    }
  };

  const handleFileUpload = async () => {
    if (!file) {
      setError('Please select a file');
      return;
    }

    setUploading(true);
    setError('');
    setSuccess('');

    try {
      const formData = new FormData();
      formData.append('file', file);
      formData.append('schema_id', schemaId);
      formData.append('entity_id', entityId);

      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/visuals/upload`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`
        },
        body: formData
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Upload failed');
      }

      setSuccess(`Successfully uploaded ${data.insertedCount} records`);
      setFile(null);
      document.getElementById('file-input').value = '';
      
      setTimeout(() => {
        navigate(`/admin/entities/${entityId}/view/${schemaId}`);
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
    }
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    const columns = schema.schema_definition.columns || [];
    const requiredColumns = columns.filter(col => col.required);
    
    for (const column of requiredColumns) {
      if (!formData[column.name] || formData[column.name] === '') {
        setError(`Required field '${column.name}' is missing`);
        return;
      }
    }

    setSubmitting(true);

    try {
      const token = localStorage.getItem('token');
      const response = await fetch(`${API_URL}/admin/data`, {
        method: 'POST',
        headers: {
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        },
        body: JSON.stringify({
          schema_id: schemaId,
          data: formData,
          entity_id: entityId
        })
      });

      const data = await response.json();

      if (!response.ok) {
        throw new Error(data.message || 'Failed to insert data');
      }

      setSuccess('Data inserted successfully!');
      
      const initialFormData = {};
      columns.forEach(column => {
        initialFormData[column.name] = '';
      });
      setFormData(initialFormData);
      
      setTimeout(() => {
        navigate(`/admin/entities/${entityId}/view/${schemaId}`);
      }, 1500);
    } catch (err) {
      setError(err.message);
    } finally {
      setSubmitting(false);
    }
  };

  const renderInput = (column) => {
    const value = formData[column.name] || '';
    
    switch (column.type) {
      case 'integer':
        return (
          <input
            type="number"
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-input"
            placeholder={`Enter ${column.name}`}
          />
        );
      
      case 'date':
        return (
          <input
            type="date"
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-input"
          />
        );
      
      case 'boolean':
        return (
          <select
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-select"
          >
            <option value="">Select...</option>
            <option value="true">Yes</option>
            <option value="false">No</option>
          </select>
        );
      
      case 'email':
        return (
          <input
            type="email"
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-input"
            placeholder={`Enter ${column.name}`}
          />
        );
      
      default:
        return (
          <input
            type="text"
            value={value}
            onChange={(e) => handleInputChange(column.name, e.target.value)}
            className="form-input"
            placeholder={`Enter ${column.name}`}
          />
        );
    }
  };

  if (loading) {
    return (
      <SidebarLayout>
        <div className="add-data-container">
          <div className="add-data-loading">
            <div className="loading-spinner"></div>
            <p>Loading schema...</p>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  if (!schema) {
    return (
      <SidebarLayout>
        <div className="add-data-container">
          <div className="add-data-error">
            <h3>Schema not found</h3>
            <p>The requested table schema could not be found.</p>
            <button 
              onClick={() => navigate(`/admin/entities/${entityId}/tables`)}
              className="back-to-tables-btn"
            >
              Back to Tables
            </button>
          </div>
        </div>
      </SidebarLayout>
    );
  }

  const columns = schema.schema_definition.columns || [];

  return (
    <SidebarLayout>
      <div className="add-data-container">
        <div className="add-data-header">
          <div>
            <h1>Add Data to {schema.table_name}</h1>
            <p>Fill in the form below to add a new record</p>
          </div>
          <button 
            onClick={() => navigate(`/admin/entities/${entityId}/tables`)}
            className="add-data-back-btn"
          >
            Back to Tables
          </button>
        </div>

        <div className="add-data-content">
          <div className="input-mode-toggle">
            <button
              type="button"
              className={`mode-btn ${inputMode === 'manual' ? 'active' : ''}`}
              onClick={() => setInputMode('manual')}
            >
              📝 Manual Entry
            </button>
            <button
              type="button"
              className={`mode-btn ${inputMode === 'upload' ? 'active' : ''}`}
              onClick={() => setInputMode('upload')}
            >
              📁 Upload File
            </button>
          </div>

          {inputMode === 'manual' ? (
            <form onSubmit={handleSubmit} className="add-data-form">
              <div className="form-section">
                <h2>Record Information</h2>
                <div className="form-grid">
                  {columns.map((column, index) => (
                    <div key={index} className="form-group">
                      <label htmlFor={column.name}>
                        {column.name}
                        {column.required && <span className="required-asterisk"> *</span>}
                      </label>
                      {renderInput(column)}
                      <div className="field-info">
                        <span className="field-type">{column.type}</span>
                        {column.required && <span className="required-badge">Required</span>}
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </form>
          ) : (
            <div className="upload-section">
              <h2>Upload Data File</h2>
              <div className="upload-form">
                <div className="form-group">
                  <label>Select File:</label>
                  <input
                    id="file-input"
                    type="file"
                    accept=".csv,.xlsx,.xls"
                    onChange={handleFileChange}
                    className="file-input"
                  />
                  <div className="file-info">
                    <p>Supported formats: CSV, Excel (.xlsx, .xls)</p>
                    <p>Maximum file size: 10MB</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          <div className="form-actions">
            <button
              type="button"
              onClick={() => navigate(`/admin/entities/${entityId}/tables`)}
              className="cancel-btn"
            >
              Cancel
            </button>
            {inputMode === 'manual' ? (
              <button
                type="submit"
                disabled={submitting}
                className="submit-btn"
                onClick={handleSubmit}
              >
                {submitting ? 'Adding Data...' : 'Add Data'}
              </button>
            ) : (
              <button
                type="button"
                onClick={handleFileUpload}
                disabled={!file || uploading}
                className="submit-btn"
              >
                {uploading ? 'Uploading...' : 'Upload File'}
              </button>
            )}
          </div>

          {error && (
            <div className="error-message">
              {error}
            </div>
          )}

          {success && (
            <div className="success-message">
              {success}
            </div>
          )}
        </div>
      </div>
    </SidebarLayout>
  );
};

export default AdminAddDataPage;

